import {
    ObjectStoreNames,
    RecordMetadata,
    RecordMetadataRecorderKind,
    SESSION_PREFIX,
} from '../../../../../shared/features/download-record';
import { IndexedDatabase } from '../../../../../shared/features/download-record/helpers/indexed-db';
import { fixRecordChunksAndDownload } from '../../../../../shared/features/download-record/sagas';
import { isError } from '../../../../../shared/helpers/errors';
import { getRecordFileName } from '../../../../../shared/helpers/get-record-file-name';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../../shared/helpers/metrika';
import { FileRecorder, FileRecorderClosePayload, FileRecorderCloseResult, FileRecorderOptions } from './types';

const logger = createLogger('recorder:indexedDB');

export class IndexedDbRecorder implements FileRecorder {
    private readonly startDate: number;
    private readonly db: IndexedDatabase;
    private readonly options: FileRecorderOptions;
    private readonly recorderKind: RecordMetadataRecorderKind;
    private chunkCount = 0;

    constructor(options: FileRecorderOptions) {
        this.startDate = Date.now();

        this.db = new IndexedDatabase(
            `${SESSION_PREFIX}${this.startDate}`,
            [ObjectStoreNames.Recording, ObjectStoreNames.Metadata],
            2
        );

        this.options = options;
        this.recorderKind = options.recorderKind || 'indexeddb';
    }

    private buildMetadata(duration: number, status: RecordMetadata['status']): RecordMetadata {
        return {
            uid: this.options.uid,
            startDate: this.startDate,
            duration,
            chunkCount: this.chunkCount,
            status,
            recorderKind: this.recorderKind,
            updatedAt: Date.now(),
        };
    }

    async init() {
        await this.setMetadata(this.buildMetadata(0, 'recording'));
    }

    async addChunk(chunk: Blob): Promise<void> {
        try {
            await this.db.put(ObjectStoreNames.Recording, this.chunkCount, chunk);
            this.chunkCount += 1;

            await this.db.put(
                ObjectStoreNames.Metadata,
                'data',
                this.buildMetadata(this.chunkCount * this.options.chunkDuration, 'recording')
            );
        } catch (error) {
            if (isError(error)) {
                const stopReason = error?.name === 'QuotaExceededError' ? 'no_space' : 'other_error';
                countMetrika('error', 'local_record_errors', stopReason);
            }

            throw error;
        }
    }

    private async setMetadata(metadata: RecordMetadata) {
        await this.db.put(ObjectStoreNames.Metadata, 'data', metadata);
    }

    private async getAllChunks(): Promise<Blob[]> {
        return this.db.getAll<Blob>(ObjectStoreNames.Recording);
    }

    async close(options?: FileRecorderClosePayload): Promise<FileRecorderCloseResult> {
        if (options?.withoutSave) {
            this.db.close();
            await this.db.removeDatabase();
            return { status: 'discarded' };
        }

        const duration = Date.now() - this.startDate;

        if (options?.preserveForRecovery) {
            try {
                await this.setMetadata(this.buildMetadata(duration, 'recoverable'));
            } catch (error) {
                logger.error(error, { method: 'close:preserve-for-recovery' });
            } finally {
                this.db.close();
            }

            return { status: 'recoverable' };
        }

        let shouldRemoveDatabase = false;

        try {
            const { mimeType } = this.options;
            await this.setMetadata(this.buildMetadata(duration, 'recording'));

            const recordChunks = await this.getAllChunks();
            if (!recordChunks.length) {
                shouldRemoveDatabase = true;
                return { status: 'discarded' };
            }

            await fixRecordChunksAndDownload({
                chunks: recordChunks,
                fileName: getRecordFileName(this.startDate),
                mimeType,
                duration,
            });

            await this.setMetadata(this.buildMetadata(duration, 'saved'));
            return { status: 'saved' };
        } catch (error) {
            logger.error(error, {
                method: 'close',
            });

            try {
                await this.setMetadata(this.buildMetadata(duration, 'recoverable'));
            } catch (metadataError) {
                logger.error(metadataError, { method: 'close:metadata-recoverable' });
            }

            return { status: 'recoverable' };
        } finally {
            this.db.close();
            if (shouldRemoveDatabase) {
                try {
                    await this.db.removeDatabase();
                } catch (error) {
                    logger.error(error, { method: 'close:remove-empty-database' });
                }
            }
        }
    }
}
